import type { BacktestExitReason, BacktestPosition } from '@fleece/models';

export interface StackedBin {
  readonly x0: number;
  readonly x1: number;
  readonly counts: Readonly<Record<BacktestExitReason, number>>;
  readonly total: number;
}

const EMPTY: Record<BacktestExitReason, number> = { take_profit: 0, stop_loss: 0, expired: 0, open: 0 };

/**
 * The smallest and largest of a list, in one pass. Not `Math.min(...values)`: spreading an
 * array passes every element as an argument, and past the engine's argument limit — around
 * a hundred thousand — that throws a RangeError rather than answering.
 */
function extent(values: ReadonlyArray<number>): readonly [number, number] {
  let low = Infinity;
  let high = -Infinity;
  for (const value of values) {
    low = Math.min(low, value);
    high = Math.max(high, value);
  }
  return [low, high];
}

/**
 * Closed positions binned by a value, each bin split by exit reason. Bins are `step`
 * wide from a multiple of `step`, so two runs bin the same value the same way and are
 * comparable side by side.
 */
export function stackedBins(positions: ReadonlyArray<BacktestPosition>, value: (position: BacktestPosition) => number | undefined, step: number): StackedBin[] {
  const values = positions.flatMap((position) => {
    const v = value(position);
    return v === undefined || position.exitReason === 'open' ? [] : [{ v, reason: position.exitReason }];
  });
  if (values.length === 0) {
    return [];
  }
  const [low, high] = extent(values.map(({ v }) => v));
  const first = Math.floor(low / step);
  const last = Math.floor(high / step);
  const bins = Array.from({ length: last - first + 1 }, (_, i) => ({ x0: (first + i) * step, x1: (first + i + 1) * step, counts: { ...EMPTY }, total: 0 }));
  for (const { v, reason } of values) {
    const bin = bins[Math.floor(v / step) - first];
    bin.counts[reason] += 1;
    bin.total += 1;
  }
  return bins;
}

/** A bin width that gives roughly `target` bins over the range, from a 1-2-5 ladder. */
export function niceStep(values: ReadonlyArray<number>, target = 12): number {
  if (values.length === 0) {
    return 1;
  }
  const [low, high] = extent(values);
  const span = high - low;
  if (span === 0) {
    return 1;
  }
  const raw = span / target;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const ladder = [1, 2, 5, 10].map((m) => m * magnitude);
  return ladder.find((step) => step >= raw) ?? ladder[ladder.length - 1];
}
